import type { Editor } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';

import { useI18n } from '../../../../../i18n';
import { PAGE, PAGE_HEADER, PAGE_FOOTER } from '../../../../../config/documentStyles';
import type { Documento, DocumentVersion, EstadoDocumento } from '../../../../../types';
import { ContentPages } from '../viewer/ContentPages';
import { ViewerStyles } from '../viewer';
import type { ContentBlock } from '../viewer';
import { CopilotOverlay } from '../../../../platform/ai/CopilotOverlay';
import { panelStyles } from './document-panel-styles';

interface DocumentContentViewProps {
  doc: Documento;
  estado: EstadoDocumento;
  selectedVersion: DocumentVersion | null;
  isEditing: boolean;
  editor: Editor | null;
  zoom: number;
  contentHtml: string;
  contentBlocksA4: ContentBlock[];
  useContentPagesMode: boolean;
  copilotEnabled: boolean;
  editionLabel: string;
  dateLabel: string;
}

export function DocumentContentView({
  doc,
  estado,
  selectedVersion,
  isEditing,
  editor,
  zoom,
  contentHtml,
  contentBlocksA4,
  useContentPagesMode,
  copilotEnabled,
  editionLabel,
  dateLabel,
}: DocumentContentViewProps) {
  const { t } = useI18n();

  if (useContentPagesMode && !isEditing) {
    return (
      <div style={{ flex: 1, overflow: 'auto' }}>
        <ViewerStyles />
        <ContentPages
          contentBlocks={contentBlocksA4}
          editing={false}
          editor={null}
          zoom={zoom}
          coverPageProps={{
            doc: doc,
            estado,
            latestVersion: selectedVersion,
            renderPageFooter: (pageNum: number, sectionTitle: string) => (
              <div style={footerStyle}>
                <span style={footerTextClamp}>{sectionTitle || doc.codigo}</span>
                <span>
                  {t.common.page} {pageNum}
                </span>
              </div>
            ),
            totalPages: 999,
          }}
          renderPageHeader={() => (
            <div style={pageHeaderStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={PAGE_HEADER.logoSrc}
                  alt=""
                  style={{ height: PAGE_HEADER.logoHeight, width: 'auto' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <span style={{ color: PAGE_HEADER.mutedColor }}>{doc.empresa ?? ''}</span>
              </div>
              <span style={pageHeaderTitle}>{doc.nombre}</span>
              <div style={{ textAlign: 'right', color: PAGE_HEADER.mutedColor }}>
                <div>{dateLabel}</div>
                <div>{editionLabel}</div>
              </div>
            </div>
          )}
          renderPageFooter={(pageNum: number, sectionTitle: string) => (
            <div style={footerStyle}>
              <span style={footerTextClamp}>{sectionTitle || doc.codigo}</span>
              <span>
                {t.common.page} {pageNum}
              </span>
            </div>
          )}
        />
      </div>
    );
  }

  return (
    <div style={panelStyles.contentScroll}>
      <div
        data-a4-page
        style={{
          ...panelStyles.a4Container,
          transform: `scale(${zoom / 100})`,
          transformOrigin: 'top center',
          marginBottom: zoom < 100 ? -(PAGE.height * (1 - zoom / 100)) : 0,
        }}
      >
        {/* A4 Page Header (read mode only) */}
        {!isEditing && (
          <div style={panelStyles.pageHeader}>
            <div style={panelStyles.pageHeaderLeft}>
              <img
                src={PAGE_HEADER.logoSrc}
                alt="V\u00B7IANet"
                style={panelStyles.pageHeaderLogo}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <span style={panelStyles.pageHeaderDocName}>{doc.nombre}</span>
            </div>
            <div style={panelStyles.pageHeaderRight}>
              <span style={panelStyles.pageHeaderMeta}>{editionLabel}</span>
              {dateLabel && (
                <>
                  <span style={panelStyles.pageHeaderSep}>|</span>
                  <span style={panelStyles.pageHeaderMeta}>{dateLabel}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Document content */}
        {isEditing && editor ? (
          <div className="viewer-content">
            <EditorContent editor={editor} />
          </div>
        ) : (
          <CopilotOverlay codigoDocumento={doc.codigo} enabled={copilotEnabled && !isEditing}>
            <div className="viewer-content" dangerouslySetInnerHTML={{ __html: contentHtml }} />
          </CopilotOverlay>
        )}
        {!contentHtml && !isEditing && (
          <p style={panelStyles.placeholderText}>{t.common.noTieneContenido}</p>
        )}

        {/* A4 Page Footer (read mode only) */}
        {!isEditing && (
          <div style={panelStyles.pageFooter}>
            <span style={panelStyles.pageFooterText}>
              {doc.codigo} {doc.nombre}
            </span>
            <span style={panelStyles.pageFooterText}>{t.common.page} 1 / 1</span>
          </div>
        )}
      </div>
    </div>
  );
}

const footerStyle: React.CSSProperties = {
  padding: `8px ${PAGE.padSide}px 16px`,
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: PAGE_FOOTER.fontSize,
  color: PAGE_FOOTER.color,
  fontFamily: PAGE_FOOTER.fontFamily,
  borderTop: PAGE_FOOTER.borderTop,
  flexShrink: 0,
};

const footerTextClamp: React.CSSProperties = {
  maxWidth: 300,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const pageHeaderStyle: React.CSSProperties = {
  padding: `${PAGE.padTop * 0.27}px ${PAGE.padSide}px 8px`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: PAGE_HEADER.borderBottom,
  fontFamily: PAGE_HEADER.fontFamily,
  fontSize: PAGE_HEADER.fontSize,
  flexShrink: 0,
};

const pageHeaderTitle: React.CSSProperties = {
  color: PAGE_HEADER.accentColor,
  fontWeight: 600,
  maxWidth: 300,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textAlign: 'center',
};
